// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { listAssetGroups, getAssetGroup } from "../graphql/queries";
import { generateClient } from "aws-amplify/api";
import {
  createAssetGroup,
  deleteAssetGroup,
  updateAssetGroup,
} from "../graphql/mutations";
import { schemaMatch } from "../util/utils";
import { listAllPages } from "../utils/graphqlPagination";
import {
  assetOrganizationsByGroupCustom,
  updateComposedForecastAssetsCustom,
  updateGeoPointOrganizationCustom,
  updateProcessedModelSchemaAssetsOrganizationCustom,
} from "../graphql/customQueries";

const client = generateClient();

// Groups are small rows, so a large page keeps the round trips down
const ASSET_GROUP_PAGE_SIZE = 1000;

const initialState = {
  loading: "idle",
  error: null,
  groups: {},
};

export const postAssetGroup = createAsyncThunk(
  "assetGroup/postAssetGroup",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: createAssetGroup,
        variables: { input },
      });

      return data.createAssetGroup;
    } catch (error) {
      console.log("post group error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Craig, this one
export const fetchAssetGroupById = createAsyncThunk(
  "assetGroup/fetchAssetGroupById",
  async (id, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: getAssetGroup,
        variables: { id },
      });
      return data.getAssetGroup;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchAssetGroups = createAsyncThunk(
  "assetGroup/fetchAllAssetGroups",
  async (_, thunkAPI) => {
    try {
      // Every page: AppSync stops at one page, and its owner/org filter runs after the read, so a member could see
      // only some of their groups once the table passes a page
      const { items } = await listAllPages(listAssetGroups, "listAssetGroups", { limit: ASSET_GROUP_PAGE_SIZE }, {
        label: "asset groups",
      });
      const groupDict = items.reduce(
        (acc, curr) => ({ ...acc, [curr.id]: curr }),
        {}
      );
      return groupDict;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteAssetGroupFunc = createAsyncThunk(
  "assetGroup/deleteAssetGroup",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: deleteAssetGroup,
        variables: { input },
      });

      return data.deleteAssetGroup;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const editAssetGroupFunc = createAsyncThunk(
  "assetGroup/editAssetGroup",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: updateAssetGroup,
        variables: { input },
      });
      return data.updateAssetGroup;
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Org members read an asset, and its composed-forecast and model links, only through the row's own organizationId, so a
// group's org change has to reach every asset and link in it (updating the group row alone leaves them unreadable).
// Only rows whose org differs are written. Admin-only in practice: the group's org picker is Admin-only.
export const cascadeAssetGroupOrganization = createAsyncThunk(
  "assetGroup/cascadeOrganization",
  async ({ assetGroupId, organizationId }: { assetGroupId: string; organizationId: string | null }, thunkAPI) => {
    try {
      const assets = [];
      let nextToken = null;
      do {
        const { data } = await client.graphql({
          query: assetOrganizationsByGroupCustom,
          variables: { assetGroupId, nextToken },
        });
        assets.push(...data.geoPointsByAssetGroupIdAndName.items.filter(Boolean));
        nextToken = data.geoPointsByAssetGroupIdAndName.nextToken;
      } while (nextToken);

      const target = organizationId || null;
      const differs = (row) => (row?.organizationId || null) !== target;
      const settled = await Promise.allSettled([
        ...assets.filter(differs).map((asset) =>
          client.graphql({
            query: updateGeoPointOrganizationCustom,
            variables: { input: { id: asset.id, organizationId: target } },
          })
        ),
        ...assets
          .flatMap((asset) => asset.linkedComposedForecasts?.items ?? [])
          .filter(differs)
          .map((link) =>
            client.graphql({
              query: updateComposedForecastAssetsCustom,
              variables: { input: { id: link.id, organizationId: target } },
            })
          ),
        ...assets
          .flatMap((asset) => asset.linkedProcessedModelSchemas?.items ?? [])
          .filter(differs)
          .map((link) =>
            client.graphql({
              query: updateProcessedModelSchemaAssetsOrganizationCustom,
              variables: { input: { id: link.id, organizationId: target } },
            })
          ),
      ]);
      const failed = settled.filter((r) => r.status === "rejected");
      if (failed.length) {
        console.error("cascadeAssetGroupOrganization: some rows not updated", failed);
        return thunkAPI.rejectWithValue(`${failed.length} of ${settled.length} assets/links kept their old organization`);
      }
      return { assetGroupId, organizationId: target, updated: settled.length };
    } catch (error) {
      console.error("Error cascading asset group organization:", error);
      return thunkAPI.rejectWithValue("Failed to move the group's assets to the new organization");
    }
  }
);

const AssetGroupSlice = createSlice({
  name: "assetGroups",
  initialState,
  reducers: {
    addAssetGroup: (state, action) => {
      state.groups[action.payload.id] = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssetGroupById.pending, (state) => {
        // state.error = null;
      })
      .addCase(fetchAssetGroupById.fulfilled, (state, action) => {
        state.groups[action.payload.id] = action.payload;
        state.loading = "success";
        // state.error = null;
      })
      .addCase(fetchAssetGroupById.rejected, (state) => {
        // state.error = null;
      })
      .addCase(editAssetGroupFunc.pending, (state) => {
        // state.error = null;
      })
      .addCase(editAssetGroupFunc.fulfilled, (state, action) => {
        state.groups[action.payload.id] = action.payload;
      })
      .addCase(editAssetGroupFunc.rejected, (state) => {
        state.error = null;
      })
      .addCase(deleteAssetGroupFunc.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteAssetGroupFunc.fulfilled, (state, action) => {
        delete state.groups[action.payload.id];
      })
      .addCase(deleteAssetGroupFunc.rejected, (state) => {
        state.error = null;
      })
      .addCase(postAssetGroup.pending, (state) => {
        state.error = null;
      })
      .addCase(postAssetGroup.fulfilled, (state, action) => {
        state.loading = "success";
        state.groups[action.payload.id] = action.payload;
      })
      .addCase(postAssetGroup.rejected, (state, action) => {
        // newHire
      })
      .addCase(fetchAssetGroups.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchAssetGroups.fulfilled, (state, action) => {
        state.loading = "success";
        state.groups = action.payload;
      })
      .addCase(fetchAssetGroups.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      });
  },
});

export const { addAssetGroup } = AssetGroupSlice.actions;
export default AssetGroupSlice.reducer;
